<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { Copy, Plus, RefreshCw } from '@lucide/vue'
import {
  Button,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Section,
  Spinner,
  Table,
  useToast,
} from '@drama/ui'
import { useI18n } from 'vue-i18n'
import WaitlistStatusBadge from '../components/WaitlistStatusBadge.vue'
import {
  createManualInvite,
  listWaitlistEntries,
  regenerateManualInvite,
} from '../api/waitlist.js'

const { t } = useI18n()
const toast = useToast()
const entries = ref([])
const loading = ref(false)
const creating = ref(false)
const error = ref('')
const actionEntryId = ref(null)
const selectedIds = ref(new Set())
const form = reactive({
  email: '',
  count: 1,
})

const columns = computed(() => [
  { key: 'selection', label: t('visualGameAdmin.invitationCodes.table.select'), width: '72px', align: 'center' },
  { key: 'email', label: t('visualGameAdmin.invitationCodes.table.email'), width: '240px' },
  { key: 'invite_token', label: t('visualGameAdmin.invitationCodes.table.code'), width: '130px' },
  { key: 'status', label: t('visualGameAdmin.invitationCodes.table.status'), width: '120px' },
  { key: 'invite_state', label: t('visualGameAdmin.invitationCodes.table.inviteState'), width: '120px' },
  { key: 'invite_expires_at', label: t('visualGameAdmin.invitationCodes.table.expiresAt'), width: '190px' },
  { key: 'created_at', label: t('visualGameAdmin.invitationCodes.table.createdAt'), width: '190px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '360px' },
])

const selectableEntries = computed(() => entries.value.filter((entry) => entry.invite_token))
const selectedEntries = computed(() => selectableEntries.value.filter((entry) => selectedIds.value.has(entry.id)))
const allSelected = computed(() => (
  selectableEntries.value.length > 0 && selectedEntries.value.length === selectableEntries.value.length
))

onMounted(loadEntries)

async function loadEntries() {
  loading.value = true
  error.value = ''
  try {
    const result = await listWaitlistEntries({ source: 'manual_admin', limit: 50 })
    entries.value = result.items
    const visibleIds = new Set(result.items.map((entry) => entry.id))
    selectedIds.value = new Set([...selectedIds.value].filter((id) => visibleIds.has(id)))
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    loading.value = false
  }
}

async function createInvite() {
  creating.value = true
  error.value = ''
  try {
    const count = Number(form.count)
    const result = await createManualInvite({ email: count === 1 ? form.email || null : null, count })
    form.email = ''
    const createdCount = Array.isArray(result.items) ? result.items.length : 1
    toast.success(t('visualGameAdmin.invitationCodes.created', { count: createdCount }))
    await loadEntries()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
    toast.error(t('visualGameAdmin.invitationCodes.createFailed'), { duration: 0 })
    await loadEntries()
  } finally {
    creating.value = false
  }
}

async function generateForEntry(entry) {
  actionEntryId.value = entry.id
  error.value = ''
  try {
    const regenerated = await regenerateManualInvite(entry.id)
    entries.value = entries.value.map((item) => item.id === regenerated.id ? regenerated : item)
    toast.success(t('visualGameAdmin.invitationCodes.resent'))
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
    toast.error(t('visualGameAdmin.invitationCodes.createFailed'), { duration: 0 })
    await loadEntries()
  } finally {
    actionEntryId.value = null
  }
}

async function copyText(value) {
  await navigator.clipboard.writeText(value)
  toast.success(t('visualGameAdmin.invitationCodes.copied'))
}

async function copySelectedInvites() {
  const codes = selectedEntries.value.map((entry) => entry.invite_token)
  if (codes.length === 0) return
  await navigator.clipboard.writeText(codes.join('\n'))
  toast.success(t('visualGameAdmin.invitationCodes.batchCopied', { count: codes.length }))
}

function toggleEntry(entry, checked) {
  const next = new Set(selectedIds.value)
  if (checked) next.add(entry.id)
  else next.delete(entry.id)
  selectedIds.value = next
}

function toggleAll(checked) {
  selectedIds.value = checked
    ? new Set(selectableEntries.value.map((entry) => entry.id))
    : new Set()
}

function canResend(entry) {
  return entry.status === 'invited'
}

function formatOptional(value) {
  return value === null || value === '' ? '-' : value
}
</script>

<template>
  <div class="invitation-codes-page vga-page">
    <PageHeader :title="t('visualGameAdmin.invitationCodes.title')" />

    <Section :title="t('visualGameAdmin.invitationCodes.createTitle')">
      <form class="invite-form" @submit.prevent="createInvite">
        <FormField :label="t('visualGameAdmin.invitationCodes.email')">
          <Input v-model.trim="form.email" type="email" autocomplete="off" :disabled="form.count > 1" :placeholder="t('visualGameAdmin.invitationCodes.emailOptional')" />
        </FormField>
        <FormField :label="t('visualGameAdmin.invitationCodes.count')">
          <Input v-model.number="form.count" type="number" min="1" max="100" step="1" required />
        </FormField>
        <button class="invite-generate-button" type="button" :disabled="creating" @click="createInvite">
          <Plus :size="16" />
          <span>{{ creating ? t('visualGameAdmin.common.loading') : t('visualGameAdmin.invitationCodes.generate') }}</span>
        </button>
        <p v-if="form.count > 1" class="invite-batch-hint">{{ t('visualGameAdmin.invitationCodes.batchUnboundHint') }}</p>
      </form>
      <p v-if="error" class="vga-error">{{ error }}</p>
    </Section>

    <Section :title="t('visualGameAdmin.invitationCodes.listTitle')" :padded="false">
      <template #actions>
        <div class="invite-list-actions">
          <label class="invite-select-all">
            <input type="checkbox" :checked="allSelected" :disabled="selectableEntries.length === 0" @change="toggleAll($event.target.checked)" />
            <span>{{ t('visualGameAdmin.invitationCodes.selectAll') }}</span>
          </label>
          <Button variant="secondary" size="sm" :disabled="selectedEntries.length === 0" @click="copySelectedInvites">
            <Copy :size="14" />
            {{ t('visualGameAdmin.invitationCodes.batchCopy', { count: selectedEntries.length }) }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="loadEntries">
            <RefreshCw :size="14" />
            {{ t('visualGameAdmin.invitationCodes.refresh') }}
          </Button>
        </div>
      </template>
      <div v-if="loading && entries.length === 0" class="vga-state">
        <Spinner />
        <span>{{ t('visualGameAdmin.common.loading') }}</span>
      </div>
      <EmptyState v-else-if="entries.length === 0" :title="t('visualGameAdmin.invitationCodes.empty')" />
      <Table v-else :columns="columns" :rows="entries">
        <template #selection="{ row }">
          <input
            class="invite-row-checkbox"
            type="checkbox"
            :aria-label="t('visualGameAdmin.invitationCodes.selectCode', { code: row.invite_token || '' })"
            :checked="selectedIds.has(row.id)"
            :disabled="!row.invite_token"
            @change="toggleEntry(row, $event.target.checked)"
          />
        </template>
        <template #email="{ row }">{{ formatOptional(row.email) }}</template>
        <template #invite_token="{ row }">
          <strong v-if="row.invite_token" class="invite-code">{{ row.invite_token }}</strong>
          <span v-else class="vga-muted">{{ t('visualGameAdmin.invitationCodes.regenerateRequired') }}</span>
        </template>
        <template #status="{ row }">
          <WaitlistStatusBadge kind="status" :value="row.status" :entry-id="row.id" />
        </template>
        <template #invite_state="{ row }">
          <WaitlistStatusBadge kind="invite" :value="row.invite_state" :entry-id="row.id" />
        </template>
        <template #invite_expires_at="{ row }">{{ formatOptional(row.invite_expires_at) }}</template>
        <template #actions="{ row }">
          <div class="invite-row-actions">
            <Button v-if="row.invite_token" variant="ghost" size="sm" @click="copyText(row.invite_token)">
              <Copy :size="14" />
              {{ t('visualGameAdmin.invitationCodes.copyCode') }}
            </Button>
            <Button v-if="row.invite_url" variant="ghost" size="sm" @click="copyText(row.invite_url)">
              <Copy :size="14" />
              {{ t('visualGameAdmin.invitationCodes.copyLink') }}
            </Button>
            <Button
              v-if="canResend(row)"
              variant="secondary"
              size="sm"
              :loading="actionEntryId === row.id"
              @click="generateForEntry(row)"
            >
              {{ t('visualGameAdmin.invitationCodes.regenerate') }}
            </Button>
          </div>
        </template>
      </Table>
    </Section>
  </div>
</template>

<style scoped>
.invite-form {
  display: grid;
  grid-template-columns: minmax(280px, 520px) 140px auto;
  align-items: end;
  gap: var(--drama-space-md);
}

.invite-list-actions {
  display: flex;
  align-items: center;
  gap: var(--drama-space-xs);
}

.invite-select-all {
  display: inline-flex;
  align-items: center;
  gap: var(--drama-space-xs);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  cursor: pointer;
}

.invite-select-all input,
.invite-row-checkbox {
  width: 16px;
  height: 16px;
  accent-color: var(--drama-color-primary);
  cursor: pointer;
}

.invite-select-all input:disabled,
.invite-row-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.invite-batch-hint {
  grid-column: 1 / -1;
  margin: calc(var(--drama-space-sm) * -1) 0 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.invite-generate-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--drama-space-sm);
  min-height: var(--drama-control-height-md);
  padding: var(--drama-space-sm) var(--drama-space-lg);
  border: 1px solid transparent;
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-primary);
  color: var(--drama-color-on-primary);
  font: inherit;
  font-weight: var(--drama-font-weight-medium);
  cursor: pointer;
}

.invite-generate-button:hover:not(:disabled) {
  background: var(--drama-color-primary-hover);
}

.invite-generate-button:disabled {
  opacity: 0.55;
  cursor: wait;
}

.invite-row-actions {
  display: flex;
  align-items: center;
  gap: var(--drama-space-xs);
  white-space: nowrap;
}

.invite-code {
  font-family: var(--drama-font-family-mono);
  letter-spacing: 0.08em;
}

@media (max-width: 960px) {
  .invite-form {
    grid-template-columns: 1fr;
  }

}
</style>
